<template>
  <!--   All these pixel counts subtract the headers, the tabs, and top padding so that the page will try
         to fit it all within the page as best it can  -->
  <div style="height: calc(100vh - 40px - 24px - 34px)">
    <PageHeader>
      <template #header-title>
        {{ contactsCount != null && contactsCount > 0 ? `${contactsCount} ` : ''
        }}{{ $t('Trusted contacts') }}
      </template>
      <template #header-actions>
        <a
          v-cloak
          :href="laravelNamedRoute(namedRoutes.contacts.applied)"
          class="tw-flex tw-flex-row tw-gap-2 tw-items-center tw-border tw-border-solid tw-border-gray-300 tw-rounded-2xl tw-px-3 tw-py-1"
        >
          <span class="tw-font-medium tw-text-lg tw-text-gray-800">
            {{ $t('Requests to join') }}
          </span>
          <span
            class="tw-font-bold tw-bg-red-600 tw-text-sm tw-text-white tw-rounded-2xl tw-px-2 tw-py-0.5"
          >
            {{ totalRequestsToJoin }}
          </span>
        </a>
        <WdsButton secondary data-test="share-link-button" @click="openInviteLinkModal">
          <WdsStack align-center gap-sm>
            <WdsIcon :size="16" :name="WdsIconNameEnum.LINK" />
            {{ $t('Share link') }}
          </WdsStack>
        </WdsButton>
        <WdsButton primary data-test="invite-button" @click="toggleInviteModal">
          <WdsStack align-center gap-xs>
            <WdsIcon :size="18" :name="WdsIconNameEnum.PLUS" />
            {{ $t('Invite') }}
          </WdsStack>
        </WdsButton>
        <WdsMenu :z-index="21" placement="bottom-end">
          <template #trigger>
            <WdsButtonGhost data-test="option-menu">
              <WdsIcon name="overflow-menu-horizontal" class="tw-cursor-pointer" />
            </WdsButtonGhost>
          </template>
          <WdsMenuOption data-test="bulk-import" @click="openImportModal">
            <WdsIcon :size="16" name="user-profile" />
            {{ $t('Import contacts in bulk') }}
          </WdsMenuOption>
        </WdsMenu>
      </template>
    </PageHeader>

    <ContactsGraphqlTable
      v-model:total-count="contactsCount"
      @edit="openManageModal(normalizeRow($event))"
      @delete="openDeleteModal(normalizeRow($event))"
      @re-invite="openReInviteModal(normalizeRow($event))"
      @approved="refetchContacts"
      @approve-error="handleApproveError"
      @tab-toggled="toggleActiveTab"
    >
      <template #no-results>
        <NoTrustedContactsFound>
          <template #actions>
            <WdsButton secondary data-test="invite-contact-button" @click="toggleInviteModal">
              {{ $t('Invite contact') }}
            </WdsButton>
            <WdsButton primary :href="laravelNamedRoute(namedRoutes.frontend.flows.hiring.actions)">
              {{ $t('Create a job') }}
            </WdsButton>
          </template>
        </NoTrustedContactsFound>
      </template>
    </ContactsGraphqlTable>

    <TrustedContactInviteLink
      :show="inviteLinkModalIsOpen"
      @close="inviteLinkModalIsOpen = false"
    />
    <TrustedContactImportModal :show="importModalIsOpen" @close="closeImportModal" />
    <TrustedContactManageModal
      :show="manageModalIsOpen"
      :account-id="accountId"
      :trusted-contact-id="selectedTrustedContactId"
      data-test="trusted-contact-manage-modal"
      @cancel="closeManageModal"
      @close="closeManageModal"
      @added="refetchContacts"
      @updated="refetchContacts"
    />

    <TrustedContactReInviteModal
      :show="reInviteModalIsOpen"
      :trusted-contact-id="selectedTrustedContactId"
      @close="closeReInviteModal"
      @invited="refetchContacts"
    />

    <TrustedContactDeleteModal
      :trusted-contact-id="selectedTrustedContactId"
      :show="deleteModalIsOpen"
      data-test="trusted-contact-delete-modal"
      @cancel="closeDeleteModal"
      @close="closeDeleteModal"
      @deleted="refetchContacts"
    />
  </div>
</template>

<script setup>
import { useApolloClient } from '@vue/apollo-composable'
import { computed, onMounted, ref } from 'vue'
import {
  WdsMenu,
  WdsMenuOption,
  WdsStack,
  WdsIcon,
  WdsButton,
  WdsButtonGhost,
  WdsIconNameEnum,
} from '@worksome/wds-components'

import { $t } from '@/composables/translation'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { TrustedContactOrigin } from '@/components/Contacts/CompanyFreelancer/types'
import ContactsGraphqlTable from '@/components/Contacts/ContactsGraphqlTable.vue'
import TrustedContactInviteLink from '@/components/Contacts/CompanyFreelancer/TrustedContactInviteLink.vue'
import TrustedContactImportModal from '@/components/Contacts/CompanyFreelancer/TrustedContactImportModal.vue'
import TrustedContactManageModal from '@/components/Contacts/CompanyFreelancer/TrustedContactManageModal.vue'
import TrustedContactReInviteModal from '@/components/Contacts/CompanyFreelancer/TrustedContactReInviteModal.vue'
import TrustedContactDeleteModal from '@/components/Contacts/CompanyFreelancer/TrustedContactDeleteModal.vue'
import NoTrustedContactsFound from '@/components/Contacts/CompanyFreelancer/NoTrustedContactsFound.vue'
import { NetworkError } from '@/utils/notification'
import useQueryEncoder from '@/composables/useQueryEncoder'
import PageHeader from '@/components/PageHeader.vue'

defineProps({
  totalRequestsToJoin: {
    type: [Number, String],
    required: true,
  },
})

const accountId = computed(() => currentAccount.globalId)
const { resolveClient } = useApolloClient()

const inviteLinkModalIsOpen = ref(false)
const importModalIsOpen = ref(false)
const manageModalIsOpen = ref(false)
const reInviteModalIsOpen = ref(false)
const forOrganisation = ref(false)
const deleteModalIsOpen = ref(false)
const selectedTrustedContactId = ref(null)
const contactsCount = ref(undefined)

const inviteTrustedContact = useQueryEncoder('invite-trusted-contact')

const refetchContacts = () => {
  resolveClient('publicApi')?.refetchQueries({
    include: ['TrustedContactsList', 'OrganisationTrustedContactsList'],
  })
}

/** Normalize row from action column (fragment with id, worker) to shape expected by modals */
const normalizeRow = (row) =>
  row
    ? {
        id: row.id,
        workerId: row.worker?.id ?? row.workerId,
      }
    : null

const openManageModal = (row) => {
  if (!row?.id) return
  selectedTrustedContactId.value = row.id
  manageModalIsOpen.value = true
}
const closeManageModal = () => {
  manageModalIsOpen.value = false
  selectedTrustedContactId.value = null
}

const openDeleteModal = (row) => {
  if (!row?.id) return
  selectedTrustedContactId.value = row.id
  deleteModalIsOpen.value = true
}
const closeDeleteModal = () => {
  deleteModalIsOpen.value = false
  selectedTrustedContactId.value = null
}

const openReInviteModal = (row) => {
  if (!row?.id) return
  selectedTrustedContactId.value = row.id
  reInviteModalIsOpen.value = true
}
const closeReInviteModal = () => {
  reInviteModalIsOpen.value = false
  selectedTrustedContactId.value = null
}

const openInviteLinkModal = () => {
  inviteLinkModalIsOpen.value = true
}

const openImportModal = () => {
  importModalIsOpen.value = true
}
const closeImportModal = () => {
  importModalIsOpen.value = false
}

const toggleActiveTab = (optionKey) => {
  forOrganisation.value = optionKey === TrustedContactOrigin.ORGANISATION_CONTACTS
}

const toggleInviteModal = () => {
  manageModalIsOpen.value = !manageModalIsOpen.value
}

const handleApproveError = (errors) => {
  new NetworkError({
    msg: errors[0],
    errors,
  }).dispatch()
}

onMounted(() => {
  if (inviteTrustedContact.value) {
    toggleInviteModal()
  }
})
</script>
